<template>
  <div>
    <div class="max-w-screen-xl pl-4 py-4 mx-auto flex courses__container">
      <SkipToMain
        href="#courses-list"
        label="Skip to Courses List"
      />
      <SidebarWrapper
        :open="filterMenuOpened"
        class="filter-menu sidebar"
        name="Course Filters"
        @close="closeFilterMenu"
      >
        <template #sidebar-heading>
          <div>
            <div class="flex items-start">
              <h1 class="text-xl flex-1 mr-2 heading-1">Courses</h1>
              <button
                type="button"
                aria-label="Close"
                class="md:hidden"
                @click="closeFilterMenu"
              >
                <BaseIcon icon="times" />
              </button>
            </div>
            <button
              v-if="isFiltering || searchQuery"
              type="button"
              class="mb-2 bg-theme-base text-white transform hover:scale-95 transition-all duration-300 ease-in-out rounded-full px-2 py-1 pill-label text-xs"
              @click="clearFilters"
            >
              <BaseIcon icon="times-circle" />
              Clear Filters
            </button>
          </div>
        </template>
        <div class="mb-8">
          <FilterInput
            v-model="searchQuery"
            data-test="search-courses"
            placeholder="Type to search courses..."
            label="Search Courses"
            class="w-full"
            :config="{ type: 'search' }"
          >
            <template #iconStart>
              <BaseIcon
                icon="search"
                class="color-deep-black"
              />
            </template>
          </FilterInput>
        </div>
        <div
          class="overflow-y-auto"
          style="height:65vh"
        >
          <Filters
            :filters="filtersDataWithOptions"
            :values="filters"
            @input="filters = $event"
          />
        </div>
      </SidebarWrapper>
      <!-- TODO: Improve the layout to use fixed sidebar and free scroll content instead of local scrolling  -->
      <article
        id="courses-list"
        class="flex-1 px-4"
        :class="{'overflow-y-auto': !expandedData && !filterMenuOpened, 'overflow-hidden': expandedData || filterMenuOpened}"
        style="height:80vh"
      >
        <button
          type="button"
          class="filter-button bg-theme-navbar-background color-theme-navbar-link hover:opacity-80 rounded-full px-2 py-1 transition ease-out duration-200  pill-label text-xs lg:hidden mb-2"
          @click.stop="filterMenuOpened=true"
        >
          <BaseIcon icon="filter" />
          Filters
        </button>
        <div>
          <div class="flex items-top mb-2 courses__caption-filters">
            <div class="flex-1">
              <h2 class="text-2xl font-bold">Results ({{ total }})</h2>
              <button
                v-if="enableCoursesCsvExport"
                type="button"
                data-test="export-all-results-as-csv"
                class="text-sm color-theme-light hover:color-theme-base transition-all duration-300 ease-in-out"
                @click.stop="downloadCoursesAsCsv"
              >
                <BaseIcon icon="download" />
                <span class="ml-2 underline">
                  Export all results as CSV
                </span>
              </button>
            </div>
            <div class="flex-1" />
            <div class="flex items-center h-12">
              <label
                class="font-bold mr-2"
                for="sort-by-select"
              >Sort By</label>
              <FormSelect
                id="sort-by-select"
                :value="sortBy || sortByOptions[0].id"
                :config="sortByConfig"
                :custom-label="getSortByLabel"
                :hide-empty-value="true"
                @input="updateSortBy"
              />
            </div>
          </div>
          <div
            class="media-card flex rounded-lg mb-0 overflow-hidden mt-6"
            role="region"
            aria-live="polite"
            id="search-status"
          >
            <div class="content px-0 py-3 ml-6 flex-grow flex flex-col justify-center">
              <p
                v-if="!total && !isLoading && searchQuery"
                class="visually-hidden"
              >
                No Results
              </p>
              <p v-if="isLoading">
                Loading results...
              </p>
              <p v-if="!isLoading && total">
                {{ total }} results found. Showing {{ (currentPage - 1) * pageSize + 1 }} - {{ currentPage * pageSize > total ? total : currentPage * pageSize }}.
              </p>
            </div>
          </div>
          <div
            v-if="isLoading"
            class="p-20 flex justify-center"
          >
            <BaseSpinner />
          </div>
          <template v-else>
            <div v-if="coursesWithDepartment.length">
              <ul
                class="courses__list  rounded-lg overflow-hidden border border-gray-100"
                data-test="course-list-container"
              >
                <li
                  v-for="(course, i) in coursesWithDepartment"
                  :key="course.id"
                  :aria-posinset="(currentPage - 1) * 20 + i"
                  :aria-setsize="total"
                  class="course-button block w-full text-left p-6 border-b border-gray-100 cursor-pointer overflow-hidden hover:bg-gray-50"
                  :class="{ 'bg-blue-50': expandedData && expandedData.id===course.id }"
                  @click.stop="expandData(course)"
                >
                  <span
                    v-if="course.code"
                    class="pill-label text-xs mb-2 px-2 rounded bg-theme-base inline-block"
                  >{{ course.code }}</span>
                  <h3><a
                      :href="getCourseUrl(course)"
                      @click.stop.prevent="expandData(course)"
                    >{{ getCourseTitle(course) }}</a></h3>
                  <p v-html="_truncate(course.description || 'No Description', { separator: '...', length: 150 })" />
                </li>
              </ul>
              <BasePagination
                v-if="total > pageSize"
                class="mt-4"
                :current-page="currentPage"
                :total-pages="totalPages"
                @page-change="updatePage"
              />
            </div>
            <EmptyState
              v-else
              label="No Courses Found"
              class="flex-1 mt-10"
            />
          </template>
        </div>
      </article>
      <SidebarWrapper
        :open="!!expandedData"
        position="right"
        class="expand-sheet sidebar"
        name="Additional Course Information"
        data-test="course preview"
        @close="closeExpandedData"
      >
        <div
          v-if="expandedData"
          class="flex flex-col"
          style="height:96vh"
          :aria-hidden="!expandedData"
        >
          <FocusLock>
            <div class="flex items-start mb-2">
              <h1
                data-test="course title"
                class="text-2xl font-bold"
              >{{ getCourseTitle(expandedData) }}</h1>
              <button
                type="button"
                data-test="close-sidebar"
                aria-label="Close"
                @click="closeExpandedData"
              >
                <BaseIcon icon="times" />
              </button>
            </div>
            <h2
              v-if="expandedData.code"
              data-test="course-code"
              class="pill-label text-xs mb-4 px-2 rounded bg-theme-base inline-block"
            >{{ expandedData.code }}</h2>
            <div class="flex-1 overflow-y-auto content-height">
              <ResourcePreview
                v-if="configurableResourcePreview"
                class="rounded bg-white border border-gray-300"
                :value="expandedData"
                :template="coursePreviewTemplate"
              />
              <div v-else>
                <p>
                  {{ expandedData.description || '...' }}
                </p>
                <div class="border rounded mt-4 bg-white">
                  <table class="table-auto w-full">
                    <tbody>
                      <tr class="border-b">
                        <th class="py-2 info-table info-table--label px-4 text-left">Department</th>
                        <td class="py-2 info-table px-4">{{ departmentsList }}</td>
                      </tr>
                      <tr class="border-b">
                        <th class="py-2 info-table info-table--label px-4 text-left">Units</th>
                        <td class="py-2 info-table px-4">{{ creditUnits }}</td>
                      </tr>
                      <tr
                        v-if="!hideCareerSection"
                        class="border-b"
                      >
                        <th class="py-2 info-table info-table--label px-4 text-left">Career</th>
                        <td class="py-2 info-table px-4">{{ expandedData.career || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <CourseSchedule
                    v-if="sectionSchedulingRedesign"
                    :document-value="expandedData"
                    :displayed-in-table="true"
                  />
                </div>
              </div>
            </div>
            <div class="bg-gray-50 pt-6">
              <button
                class="view-course w-full rounded-md flex items-center py-4 px-6 bg-theme-base pill-label transform hover:scale-95 transition-all duration-300 ease-in-out"
                @click="$router.push(getCourseUrl(expandedData))"
              >
                <span
                  class="flex-1"
                  data-test="View Course"
                >View Course</span>
                <BaseIcon
                  icon="long-arrow-alt-right"
                  class="ml-2"
                />
              </button>
            </div>
          </FocusLock>
        </div>
      </SidebarWrapper>
    </div>
    <div class="max-w-screen-xl pl-4 mx-auto flex">
      <div class="theme-page-content flex justify-center link-container print-list-button">
        <a
          v-if="enablePrintList"
          class="link text-md"
          :href="printPath"
        >
          Download Course List as PDF
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { toRef, computed, watch, useStore } from '@nuxtjs/composition-api'
import _get from 'lodash/get'
import _truncate from 'lodash/truncate'
import { mixin as clickaway } from 'vue-clickaway'
import FocusLock from 'vue-focus-lock'
import {
  fillFiltersOptionsFromQuestions,
  mergeResourceFieldColumns,
} from '@/utils/filters'
import FormSelect from '@/components/Fields/FormSelect'
import SidebarWrapper from '~contemporaryTheme/components/SidebarWrapper'
import FilterInput from '~contemporaryTheme/components/Base/FilterInput'
import Filters from '~contemporaryTheme/components/Base/Filters'
import EmptyState from '~contemporaryTheme/components/Base/EmptyState'
import SkipToMain from '@/components/SkipToMain'
import useCourses from '@/composables/useCourses'
import useFilters from '@/composables/useFilters'
import usePrint from '@/composables/usePrint'
import ResourcePreview from '@/components/ResourcePreview'
import CourseSchedule from '@/components/Fields/CourseSchedule'
import useFeatureFlags from '@/composables/useFeatureFlags'
import {
  CONFIGURABLE_RESOURCE_PREVIEW,
  SECTION_SCHEDULING_REDESIGN,
  TITLE_INTERPOLATIONS,
  PRINT_LIST,
  COURSES_CSV_EXPORT,
} from '@/constants/featureFlags'
import { decamelize } from '@/utils/generalUtils'
import { generateCourseLabel, generateRevisionLabel } from '@/utils/dataLabels'
import useInterpolation from '@/composables/useInterpolation'
import useMeta from '@/composables/useMeta'

export default {
  setup(props, { root }) {
    const {
      configurableResourcePreview,
      sectionSchedulingRedesign,
      enableTitleInterpolations,
      enablePrintList,
      enableCoursesCsvExport,
    } = useFeatureFlags({
      configurableResourcePreview: CONFIGURABLE_RESOURCE_PREVIEW,
      sectionSchedulingRedesign: SECTION_SCHEDULING_REDESIGN,
      enableTitleInterpolations: TITLE_INTERPOLATIONS,
      enablePrintList: PRINT_LIST,
      enableCoursesCsvExport: COURSES_CSV_EXPORT,
    })
    const store = useStore()

    const terms = computed(() => store.state.terms.allTerms)
    const coursePreviewTemplate = computed(
      () => store.state.settings.coursePreviewTemplate
    )
    const additionalColumns = Object.values(
      coursePreviewTemplate.value.questions
    ).map((q) => {
      if (q.custom) return `customFields.${q.dataKey}`
      else return q.dataKey
    })

    const displayDependenciesInPreviewCard = computed(() => !!coursePreviewTemplate.value.questions.dependencies)

    const { filtersPayload, filtersData, filtersDefaultValues } = useFilters(
      root,
      {
        type: 'courses',
        prebuiltFilters: toRef(props, 'filterFields'),
      }
    )

    const baseColumns = [
      'displayName',
      'department',
      'description',
      'name',
      'courseNumber',
      'subjectCode',
      'code',
      'courseGroupId',
      'credits.creditHours',
      'career',
      ...(enableTitleInterpolations
        ? [
            'college',
            'longName',
            'status',
            'institution',
            'institutionId',
            'customFields.rawCourseId',
            'customFields.crseOfferNbr',
            'customFields.catalogAttributes',
          ]
        : []),
    ]
    const columns = mergeResourceFieldColumns(baseColumns, additionalColumns)

    const {
      isLoading,
      search,
      searchDebounced,
      fetchData,
      fetchQuestion,
      coursesWithDepartment,
      filters,
      searchQuery,
      total,
      totalPages,
      currentPage,
      updatePage,
      updateQuery,
      pageSize,
      getCourseUrl,
      courseQuestions,
      sortByOptions,
      sortBy,
      downloadCoursesAsCsv,
    } = useCourses(root, {
      filtersPayload,
      columns,
      formatDependents: displayDependenciesInPreviewCard.value,
    })

    const includeHistoricalRevisions = computed(() => {
      return filters.value.includeHistoricalRevisions
    })

    function updateSortBy(value) {
      const updatedValue = value === '-' ? '' : value

      sortBy.value = updatedValue
      updateQuery({ sortBy: updatedValue })
    }

    function clearFilters() {
      filters.value = {}
      searchQuery.value = null
    }

    const filtersDataWithOptions = computed(() => {
      return fillFiltersOptionsFromQuestions(
        filtersData.value,
        courseQuestions.value
      )
    })

    watch(
      filtersDefaultValues,
      () => {
        if (Object.values(filtersDefaultValues).length) {
          filters.value = { ...filters.value, ...filtersDefaultValues.value }
        }
      },
      { immediate: true }
    )

    const { getInterpolatedFieldValue } = useInterpolation()

    const { printPath } = usePrint(root)

    const { catalogWideMetaTitle } = useMeta()

    return {
      isLoading,
      search,
      searchDebounced,
      fetchData,
      fetchQuestion,
      coursesWithDepartment,
      filters,
      searchQuery,
      total,
      totalPages,
      currentPage,
      updatePage,
      pageSize,
      getCourseUrl,
      _truncate,
      _get,
      clearFilters,
      coursePreviewTemplate,
      questions: courseQuestions,
      filtersDataWithOptions,
      configurableResourcePreview,
      updateSortBy,
      sortBy,
      sortByOptions,
      sectionSchedulingRedesign,
      getInterpolatedFieldValue,
      enableTitleInterpolations,
      enablePrintList,
      printPath,
      catalogWideMetaTitle,
      downloadCoursesAsCsv,
      enableCoursesCsvExport,
      terms,
      includeHistoricalRevisions,
    }
  },
  components: {
    FilterInput,
    Filters,
    EmptyState,
    FocusLock,
    SkipToMain,
    SidebarWrapper,
    ResourcePreview,
    FormSelect,
    CourseSchedule,
  },
  mixins: [clickaway],
  props: {
    filterFields: {
      type: Array,
      default: () => [],
    },
    hideCareerSection: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filterMenuOpened: false,
      expandedData: null,
      showAlert: true,
    }
  },
  head() {
    return {
      title: `Courses | ${this.catalogWideMetaTitle}`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `Courses | ${this.catalogWideMetaTitle}`,
        },
      ],
    }
  },
  computed: {
    departmentsList() {
      if (!this.expandedData.department && !this.expandedData.departments)
        return '-'

      if (this.expandedData.departments.length)
        return this.expandedData.departments
          .map((d) => d.displayName)
          .join(', ')

      if (this.expandedData.department)
        return this.expandedData.department.displayName || '-'

      return '-'
    },
    sortByConfig() {
      return { options: this.sortByOptions.map(option => option.id) }
    },
    isFiltering() {
      return Object.values(this.filters).filter(
        (el) => (Array.isArray(el) ? el.length : el)
      ).length
    },
    creditUnits() {
      const min = _get(this.expandedData, 'credits.creditHours.min')
      const max = _get(this.expandedData, 'credits.creditHours.max')
      const value = _get(this.expandedData, 'credits.creditHours.value')

      if (min !== undefined || max !== undefined) {
        if (min === max || max === undefined) {
          return min
        }

        if (min === undefined && max !== undefined) {
          return max
        }

        if (min !== max && max !== undefined) {
          return `${min}-${max}`
        }
      }

      if (value !== undefined) {
        return value
      }

      return '-'
    },
  },
  watch: {
    filters: {
      deep: true,
      handler: 'searchDebounced',
    },
    '$route.query': 'fetchData',
  },
  async created() {
    this.isLoading = true
    await this.fetchQuestion()
    this.fetchData()
  },
  methods: {
    expandData(data) {
      this.expandedData = data
    },
    closeFilterMenu() {
      this.filterMenuOpened = false
    },
    closeExpandedData() {
      this.expandedData = null
    },
    decamelize,
    getCourseTitle(course) {
      const revisionLabel = this.includeHistoricalRevisions
        ? generateRevisionLabel(course, this.terms)
        : ''
      const courseLabel = generateCourseLabel(course, undefined, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
        nameOnlyFallback: true,
      })
      return `${courseLabel} ${revisionLabel}`.trim()
    },
    getSortByLabel(option) {
      const found = this.sortByOptions.find(o => o.id === option)
      return found ? found.name : ''
    },
  },
}
</script>

<style scoped>
.course-button:focus {
  @apply bg-gray-50;
}

.sidebar.filter-menu {
  @apply bg-gray-100;

  width: 320px;
}
.sidebar.expand-sheet {
  @apply bg-gray-50;

  max-width: 500px;
}
.content-height {
  max-height: calc(96vh - 250px);
}

h1 {
  color: var(--h1HeaderFontColor, var(--text-gray-900));
  font-family: var(--h1HeaderFontTypeface, inherit);
  font-weight: var(--h1HeaderFontWeight, 700);
}
h3 {
  color: var(--h3HeaderFontColor, inherit);
  font-family: var(--h3HeaderFontTypeface, inherit);
  font-weight: var(--h3HeaderFontWeight, 700);
}
p {
  color: var(--textFontColor, var(--text-gray-500));
  font-family: var(--textFontTypeface, inherit);
  font-weight: var(--textFontWeight, inherit);
}
.pill-label {
  font-family: var(--interfaceFontTypeface, inherit);
  color: white;
  font-weight: var(--interfaceFontWeight, 700);
}
.info-label {
  font-family: var(--interfaceFontTypeface, inherit);
  font-weight: var(--interfaceFontWeight, inherit);
}
.info-table {
  color: var(--interfaceFontColor, inherit);
  font-weight: var(--interfaceFontWeight, inherit);
  font-family: var(--interfaceFontTypeface, inherit);
}
.info-table--label {
  font-weight: var(--interfaceFontWeight, 700);
}
@media only screen and (min-width: 1024px) {
  .sidebar.filter-menu {
    @apply top-auto left-auto shadow-none relative h-auto rounded-lg z-0;

    transform: translateX(0);
  }
}

.link-container {
  width: 240px;
  padding-top: 28px;
  padding-bottom: 28px;
}

.link {
  cursor: pointer;
  text-decoration: underline;
  color: var(--h2HeaderFontColor, var(--text-gray-900));
  font-family: var(--h2HeaderFontTypeface, inherit);
  font-weight: var(--h2HeaderFontWeight, 700);
}
</style>
